import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";
import { coerceCapsuleId } from "../../utils/capsuleId";
import { fetchSubmitLevelDeliverable } from "../dashboard/slice";

/**
 * The learner-experience projection: the squad-shared submission facts that the
 * Build Path drawer and the re-entry guidance both read, so both read one truth.
 *
 * WHAT THIS SLICE OWNS: the projection snapshot, the scope it belongs to, its
 * freshness, and the keyed index over its outputs. Nothing else. It does NOT
 * re-own task records (state.tasks), capsule-user flags (state.user.capsuleUsers),
 * planning tasks (state.planTasks), or mission rooting (state.user.mission).
 * Those keep their existing owners and are COMPOSED at read time in
 * store/selectors/experience.js, one owner per fact.
 *
 * SESSION-ONLY. This slice is deliberately NOT in the redux-persist whitelist in
 * store/index.js. The endpoint is the only authority for what this actor may
 * read, so a persisted snapshot could show one squad's submission facts to a
 * different learner on the next session, before the first fetch lands.
 *
 * NO SERVER-DERIVED CURRENT POSITION. The response carries no active capsule, no
 * active level, and no per-output state (technical brief RULING R1): active
 * progression on this platform is per-learner, so the server cannot honestly
 * serve a squad-level "current". The tri-state derives client-side in
 * selectOutputTriState from state.user.mission, the sole owner of position.
 */

/**
 * How long a snapshot may be held before a refetch is triggered.
 *
 * The trigger compares the SERVER's generated_at against the client clock. That
 * is a comparison across two machines and it is used ONLY as a refetch trigger.
 * It must never be rendered as an age claim ("updated 4 minutes ago"): the
 * ratified design forbids browser-time freshness claims, and any displayed
 * freshness derives from generated_at alone.
 */
export const PROJECTION_STALE_TTL_MS = 10 * 60 * 1000;

/**
 * The scope a snapshot belongs to. A projection is a fact about ONE squad read
 * by ONE actor: both halves matter, because authorization is per-actor and the
 * payload is per-squad. An identity change with no logout (a different account
 * on the same tab) has to invalidate the held snapshot exactly as a squad change
 * does, and this key is what makes those the same event.
 *
 * @param {{userId: (number|string|null), squadId: (number|string|null)}} arg
 * @returns {string}
 */
export const projectionScopeKey = ({ userId, squadId } = {}) =>
  `${userId ?? ""}:${squadId ?? ""}`;

/**
 * The one keyed index over outputs. A hand-rolled keyed map, following the
 * coaching.taskStates precedent. This store has no createEntityAdapter anywhere
 * and this chip does not introduce one.
 *
 * Keys are the COERCED capsule id. Object keys are strings in JS, so a consumer
 * reads it as `outputsByCapsuleId[coerceCapsuleId(id)]`, which keeps a numeric
 * string id and a number id from ever describing two capsules. An output whose
 * capsule id will not coerce is left out of the INDEX but stays in
 * `projection.outputs`, so the ordered list the Build Path renders is never
 * silently short a level.
 */
const indexOutputs = (outputs) => {
  const index = {};
  (Array.isArray(outputs) ? outputs : []).forEach((output) => {
    const capsuleId = coerceCapsuleId(output?.capsule_id);
    if (capsuleId === null) return;
    index[capsuleId] = output;
  });
  return index;
};

export const fetchExperienceProjection = createAsyncThunk(
  "experienceProjection/fetchExperienceProjection",
  async ({ squadId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(
        `/squads/${squadId}/experience-projection`,
        "get",
      );
      return response.data.data;
    } catch (error) {
      // Reject with a SERIALIZABLE shape, never the axios error object. The
      // route collapses missing and unreadable into one byte-identical 404, so
      // a 404 is the "unauthorized or missing" state the design renders
      // honestly (no Build Path affordance, no stale level claim) rather than
      // an uncaught rejection. Everything else is the recoverable network
      // error, which keeps the journey action primary with a subordinate retry.
      const status = error?.response?.status ?? null;
      return rejectWithValue({
        status,
        kind: status === 404 ? "unauthorized" : "unavailable",
        // The server's message, or null. Learner-facing copy belongs to the
        // rendering surface, not to the store.
        message: error?.response?.data?.message ?? null,
      });
    }
  },
);

const initialState = {
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null, // { status, kind, message } | null
  requestId: null, // latest in-flight request's id (take-latest guard)
  scopeKey: null, // the scope the held snapshot belongs to
  generatedAt: null, // the SERVER's assembly timestamp, verbatim
  fetchedAt: null, // client receipt time. A trigger floor, never an age claim
  stale: false,
  projection: null, // { squad_id, generated_at, outputs: [...] }
  outputsByCapsuleId: {},
  /**
   * A MONOTONIC READ COUNTER, INCREMENTED ON EVERY FETCH START (W3-F1, brief
   * section 3 item 4).
   *
   * The completion sequence must wait for a projection that was assembled AFTER
   * the submission it is celebrating. `status === "succeeded"` cannot express
   * that: a read dispatched before the submission can fulfil after it, and the
   * sequence would reveal a rail built from facts that predate the thing that
   * just happened. `generatedAt` cannot either -- it is the SERVER's assembly
   * timestamp, so comparing it needs a clock the client does not have, and two
   * reads within one server-side second are indistinguishable.
   *
   * A counter the CLIENT increments at dispatch answers the only question the
   * sequence is actually asking: has a read STARTED since I captured this
   * number, and has it since succeeded. Captured synchronously at the
   * completion boundary, compared at `advancing`.
   *
   * IT IS BUMPED IN `pending`, NOT IN `fulfilled`. Bumping it on arrival would
   * make "a greater generation" true for the read that was already in flight
   * when the submission landed, which is the exact read that cannot be trusted.
   */
  generation: 0,
};

/**
 * Should the ONE Dashboard effect dispatch a read right now?
 *
 * Extracted as a pure function so the decision is table-tested rather than
 * reachable only through a 2,400-line component. The effect is a single call to
 * this plus the dispatch.
 *
 * It takes the raw ids rather than a precomputed next scope key so the
 * identity guard is under test too: an empty id must never produce a fetch, and
 * a key built from missing ids ("`:`") would otherwise look like a real scope.
 *
 * The state machine, walked for loops (see the fulfilled and rejected arms,
 * which no longer clear `stale`):
 *
 *   idle          -> fetch -> loading (stale cleared) -> succeeded, not stale -> stop
 *   scope change  -> fetch -> loading -> succeeded under the new key         -> stop
 *   stale set     -> fetch -> loading (stale cleared) -> succeeded           -> stop
 *   read fails    -> failed, stale unchanged. If it was false, stop: a failed
 *                    read is never retried by the clock, only by a learner
 *                    action (markProjectionStale). If a submission had set it,
 *                    exactly one more attempt runs, and `pending` clears the
 *                    flag, so the sequence terminates either way.
 *
 * Every path to a dispatch passes through `pending`, which is the ONLY writer
 * that clears `stale`, so no branch can dispatch twice for one event.
 */
export const shouldFetchProjection = ({
  userId,
  squadId,
  status,
  scopeKey,
  isStale,
}) => {
  if (!userId || !squadId) return false;
  if (status === "loading") return false;
  const nextScopeKey = projectionScopeKey({ userId, squadId });
  if (status === "idle" || scopeKey !== nextScopeKey) return true;
  return isStale === true;
};

const experienceProjectionSlice = createSlice({
  name: "experienceProjection",
  initialState,
  reducers: {
    /**
     * Mark the snapshot stale so the ONE effect refetches it.
     *
     * This is the entry point for the design's `Try again` on the recoverable
     * network-error state. A failed read is deliberately not retried by the
     * clock, so without this a learner-initiated retry would have to dispatch
     * the thunk from a second place and there would no longer be one owner of
     * the fetch. Marking the flag keeps the single dispatcher intact.
     */
    markProjectionStale: (state) => {
      state.stale = true;
    },
  },
  extraReducers: (builder) => {
    // Take-latest, the programRequestId pattern (store/dashboard/slice.js:384).
    // RTK stamps a unique requestId per dispatch and pending fires
    // synchronously, so the last dispatch is always the recorded one and an
    // out-of-order response can never overwrite a newer snapshot.
    builder.addCase(fetchExperienceProjection.pending, (state, action) => {
      const nextScopeKey = projectionScopeKey(action.meta.arg);
      if (nextScopeKey !== state.scopeKey) {
        // A DIFFERENT scope. Clear the held snapshot now, in pending, so the
        // old squad's facts can never render under the new scope while the new
        // request is in flight. Status below is `loading`, which is the state
        // the design hides the outputs affordance on.
        state.projection = null;
        state.outputsByCapsuleId = {};
        state.generatedAt = null;
        state.fetchedAt = null;
        state.scopeKey = nextScopeKey;
      }
      state.status = "loading";
      state.error = null;
      state.requestId = action.meta.requestId;
      // The read has STARTED. See `generation` on the initial state: the bump
      // belongs here and not on arrival, or the in-flight read that predates a
      // submission would satisfy a waiter looking for a newer one.
      state.generation += 1;
      // Clearing stale here is what makes ONE staleness event cost ONE request:
      // the Dashboard effect that watches staleness sees it cleared the moment
      // the fetch is dispatched, so it cannot dispatch a second one.
      state.stale = false;
    });

    builder.addCase(fetchExperienceProjection.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.status = "succeeded";
      state.error = null;
      state.projection = action.payload ?? null;
      state.generatedAt = action.payload?.generated_at ?? null;
      state.fetchedAt = Date.now();
      state.outputsByCapsuleId = indexOutputs(action.payload?.outputs);
      // `stale` is deliberately NOT cleared here. `pending` is the only clearer.
      // A submission can settle WHILE this read is in flight, and this payload
      // was assembled by the server before that submission existed. Clearing the
      // flag on arrival would swallow that staleness event and leave the drawer
      // saying not submitted while the server holds the submission.
    });

    builder.addCase(fetchExperienceProjection.rejected, (state, action) => {
      if (action.meta.requestId !== state.requestId) return;
      state.status = "failed";
      const error = action.payload ?? {
        status: null,
        kind: "unavailable",
        message: null,
      };
      state.error = error;

      if (error.kind === "unauthorized") {
        // The 404 collapse: missing and unreadable are one state, and it can
        // arrive on a SAME-scope refetch when authorization changed on the
        // SERVER (the learner was superseded out of the squad mid-session).
        // `scopeKey` cannot see that, because it only tracks client-side
        // identity and squad changes. Keeping the snapshot here would let
        // selectBuildPathState report isUnauthorized together with N output
        // rows, and the re-entry snapshot would still name a level and a
        // building title for a squad this actor may no longer read. The design
        // requires the opposite: no stale output or level claims in the
        // unauthorized state, and a cache reset on any authorization-scope
        // change.
        state.projection = null;
        state.outputsByCapsuleId = {};
        state.generatedAt = null;
        state.fetchedAt = null;
      }
      // A RECOVERABLE failure keeps the held snapshot: it is the last safe
      // snapshot the design renders while the read is merely unavailable.
      //
      // `stale` is not cleared here either, for the same reason as fulfilled. If
      // a submission set it, exactly one more attempt runs and `pending` clears
      // the flag, so this terminates rather than looping.
    });

    // Marking stale after a submission, on BOTH arms.
    //
    // The rejected arm is required, not defensive. fetchSubmitLevelDeliverable
    // wraps TWO writes in one promise (store/dashboard/slice.js:231-264): the
    // deliverable upload, then the capsule-user flag. A durable upload followed
    // by a failed flag write rejects, so a fulfilled-only rule would leave the
    // learner looking at a projection that says not submitted while the server
    // holds the submission. Refetching on rejection costs one read and renders
    // the honest server truth either way.
    //
    // The two-write conflation in that thunk is a pre-existing defect and is
    // out of this chip's scope.
    builder.addCase(fetchSubmitLevelDeliverable.fulfilled, (state) => {
      state.stale = true;
    });
    builder.addCase(fetchSubmitLevelDeliverable.rejected, (state) => {
      state.stale = true;
    });
  },
});

export const { markProjectionStale } = experienceProjectionSlice.actions;

export default experienceProjectionSlice.reducer;
